import { Avatar, Flex, Popover, Spin, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { readAllDayDate } from "../../../../../utils/date/get-date-safe.ts"
import { type CalendarFilters as CalendarFiltersType } from "../../../redux/calendar.types.ts"
import { type Event } from "../../../types/types.ts"
import { getStoredEventColor } from "../../helpers/event-custom-colors.ts"
import { CalendarTimeAndType } from "../CalendarTimeAndType.tsx"
import {
  calendarEventCardRecordParts,
  calendarEventCardText
} from "../../helpers/calendar-event-card-text.ts"
import {
  computeAvailabilityWindow,
  eventBlockPixelWidth,
  eventBlockPosition,
  hasRoomForRecordName,
  MIN_BLOCK_PX
} from "./availability-window.ts"
import { useTeamAvailabilityEvents } from "./useTeamAvailabilityEvents.ts"

const { Text } = Typography

interface CalendarAvailabilityProps {
  date: Date
  timezone: string
  filters: CalendarFiltersType
  viewAllAllowed?: boolean
  events: Event[]
  userId?: string
  onSelectEvent: (id: string) => void
}

const NAME_COLUMN_WIDTH = 200

const eventStart = (event: Event, timezone: string) =>
  event?.all_day
    ? readAllDayDate(event.start_at)
    : dayjs(event?.start_at).tz(timezone)

// The list endpoint nests created_by/participants/lead/project/category/
// contact under extended_props rather than on the event itself — confirmed
// against the real response, not just the Event type's declared shape.
// `transformEventsForCalendar` does this same merge before handing events to
// FullCalendar; this view reads the raw API response directly, so it needs
// its own copy of that flattening rather than assuming the top level already
// has these fields.
const flattenEvent = (event: Event): Event => ({
  ...event,
  ...event?.extended_props
})

const isMine = (event: Event, userId?: string) => {
  if (!userId) return true
  const flat = flattenEvent(event)
  if (flat?.created_by?.id === userId) return true
  return !!flat?.participants?.some((p) => p?.id === userId)
}

function EventPreview({
  title,
  typeLabel,
  recordName,
  timeRange,
  participantNames
}: {
  title: string
  typeLabel: string | null
  recordName: string | null
  timeRange: string
  participantNames: string[]
}) {
  return (
    <Flex vertical gap={2} className={"max-w-64"}>
      <Text strong className={"text-sm"}>
        {title}
      </Text>
      {recordName && <Text className={"text-xs"}>{recordName}</Text>}
      <CalendarTimeAndType
        className={"text-xs text-gray-500"}
        time={timeRange}
        typeLabel={typeLabel}
      />
      {!!participantNames.length && (
        <Text type={"secondary"} className={"text-xs"}>
          {participantNames.join(", ")}
        </Text>
      )}
    </Flex>
  )
}

function EventBlock({
  event,
  timezone,
  laneWidth,
  onSelectEvent,
  style
}: {
  event: Event
  timezone: string
  // Real, measured pixels for the lane this block lives in — without this,
  // "is there room for a second line" can only ever guess from the event's
  // duration, which doesn't account for how wide the window itself actually
  // renders (an 8 AM-6 PM window can be 500px or 1500px depending on the
  // viewport, and a 30-minute block is a very different number of real
  // pixels in each).
  laneWidth: number
  onSelectEvent: (id: string) => void
  style: React.CSSProperties
}) {
  const { t } = useTranslation()
  const flat = flattenEvent(event)
  const input = { title: event?.title, record: flat }
  const { title, typeLabel } = calendarEventCardText(input, t)
  const { recordName } = calendarEventCardRecordParts(input, t)
  const color =
    getStoredEventColor(event?.id)?.border_color || event?.border_color

  const widthPct = parseFloat(String(style.width)) || 0
  const blockPx = eventBlockPixelWidth(widthPct, laneWidth)
  const showRecordName = !!recordName && hasRoomForRecordName(blockPx)

  const start = event?.start_at ? dayjs(event.start_at).tz(timezone) : null
  const end = event?.end_at ? dayjs(event.end_at).tz(timezone) : null
  const timeRange =
    start && end ? `${start.format("h:mm A")} – ${end.format("h:mm A")}` : ""
  const participantNames = (flat?.participants || [])
    .map((p) => p?.full_name)
    .filter((name): name is string => !!name)

  return (
    <Popover
      trigger={["hover", "focus"]}
      placement={"top"}
      content={
        <EventPreview
          title={title}
          typeLabel={typeLabel}
          recordName={recordName}
          timeRange={timeRange}
          participantNames={participantNames}
        />
      }
    >
      <button
        type={"button"}
        onClick={() => onSelectEvent(event.id)}
        className={
          "absolute top-1 bottom-1 overflow-hidden rounded-md border px-1.5 py-1 text-left transition-shadow hover:shadow-sm hover:z-10"
        }
        style={{
          ...style,
          minWidth: MIN_BLOCK_PX,
          backgroundColor: event?.background_color,
          borderColor: color,
          color: event?.text_color
        }}
      >
        {/* Explicit, tight line-heights rather than the inherited antd
            Typography default — that default is a *ratio* of font-size
            (≈1.57), so an 11px/10px line here still rendered at ≈17px/16px,
            and two of those (≈33px) didn't fit the ≈32px this block actually
            had to give them, clipping the second line's descenders at the
            bottom regardless of how much horizontal room it had. A fixed px
            line-height makes the space each line needs predictable, instead
            of a ratio nobody sized the block against. */}
        <Text
          ellipsis
          className={"block text-[11px] font-medium"}
          style={{ color: event?.text_color, lineHeight: "14px" }}
        >
          {title}
        </Text>
        {/* Only rendered once the block has been measured wide enough for
            it — never a mid-word ellipsis fragment. */}
        {showRecordName && (
          <Text
            ellipsis
            className={"block text-[10px] opacity-80"}
            style={{ color: event?.text_color, lineHeight: "13px" }}
          >
            {recordName}
          </Text>
        )}
      </button>
    </Popover>
  )
}

function AvailabilityRow({
  name,
  events,
  timezone,
  day,
  timeWindow,
  laneWidth,
  onSelectEvent
}: {
  name: string
  events: Event[]
  timezone: string
  day: dayjs.Dayjs
  timeWindow: { startHour: number; endHour: number }
  laneWidth: number
  onSelectEvent: (id: string) => void
}) {
  const allDayEvents = events.filter((e) => e.all_day)
  const timedEvents = events.filter((e) => !e.all_day)

  return (
    <Flex
      align={"stretch"}
      className={"border-b border-gray-100 last:border-b-0"}
    >
      <Flex
        align={"center"}
        gap={8}
        className={"sticky left-0 z-10 shrink-0 bg-white py-2 pr-3"}
        style={{ width: NAME_COLUMN_WIDTH }}
      >
        <Avatar size={"small"}>{name?.[0]?.toUpperCase() || "?"}</Avatar>
        <div className={"min-w-0"}>
          <Text ellipsis className={"block text-sm font-medium"}>
            {name}
          </Text>
          {!events.length && (
            <Text className={"text-xs text-teal-600"}>● Available</Text>
          )}
          {!!allDayEvents.length && (
            <Text ellipsis className={"block text-xs text-gray-500"}>
              All day: {allDayEvents.map((e) => e.title).join(", ")}
            </Text>
          )}
        </div>
      </Flex>

      {/* Tall enough for a full title + record line at their explicit
          line-heights (see EventBlock) with real margin to spare — the row's
          own height, shared by every block in it, is what actually
          determines whether that second line fits; a block's *width* alone
          (already handled by hasRoomForRecordName) says nothing about
          whether the row it's sitting in is tall enough to show it without
          clipping. This is a fixed height for the row, not the individual
          event — duration still only ever changes a block's width. */}
      <div className={"relative min-h-14 flex-1 py-2"}>
        {timedEvents.map((event) => {
          const position = eventBlockPosition(event, day, timeWindow, timezone)
          if (!position) return null
          return (
            <EventBlock
              key={event.id}
              event={event}
              timezone={timezone}
              laneWidth={laneWidth}
              onSelectEvent={onSelectEvent}
              style={{
                left: `${position.leftPct}%`,
                width: `${position.widthPct}%`
              }}
            />
          )
        })}
      </div>
    </Flex>
  )
}

export function CalendarAvailability({
  date,
  timezone,
  filters,
  viewAllAllowed,
  events,
  userId,
  onSelectEvent
}: CalendarAvailabilityProps) {
  const day = useMemo(() => dayjs(date).tz(timezone), [date, timezone])
  const hasTeamFilter = !!filters?.participants?.length
  const showTeamView = hasTeamFilter || !!viewAllAllowed

  const { members, eventsByMemberId, loading, error, pendingCount } =
    useTeamAvailabilityEvents({
      enabled: showTeamView,
      date,
      timezone,
      filterParticipants: hasTeamFilter ? filters.participants : null
    })

  // The solo fallback reads from the events this page has already loaded for
  // the main calendar — nothing this user isn't already allowed to see.
  const soloEvents = useMemo(() => {
    if (showTeamView) return []
    return (events || [])
      .filter((event) => isMine(event, userId))
      .filter((event) => {
        const start = eventStart(event, timezone)
        return start?.isValid() && start.isSame(day, "day")
      })
  }, [showTeamView, events, userId, timezone, day])

  const rows = useMemo(
    () =>
      showTeamView
        ? members.map((m) => ({
            id: m.id,
            name: m.name,
            events: eventsByMemberId[m.id] || []
          }))
        : [{ id: userId || "me", name: "You", events: soloEvents }],
    [showTeamView, members, eventsByMemberId, userId, soloEvents]
  )

  const totalEvents = rows.reduce((sum, row) => sum + row.events.length, 0)

  // Built from `rows`, not `eventsByMemberId` directly — the solo fallback's
  // events never appear in `eventsByMemberId` (the hook that produces it is
  // disabled in that branch), so the window has to be driven by whatever is
  // actually being displayed, team view or solo.
  const timeWindow = useMemo(() => {
    const eventsByRowId = Object.fromEntries(
      rows.map((row) => [row.id, row.events])
    )
    return computeAvailabilityWindow(eventsByRowId, day, timezone)
  }, [rows, day, timezone])

  const hourTicks = useMemo(() => {
    const ticks: number[] = []
    for (let h = timeWindow.startHour; h <= timeWindow.endHour; h += 1)
      ticks.push(h)
    return ticks
  }, [timeWindow])

  // The one real measurement every row's blocks size themselves against —
  // taken off the hour-ticks header, since every row's own lane renders at
  // that exact same width. A ResizeObserver rather than a one-off read: the
  // sidebar can resize (window resize, a panel toggle) without a navigation
  // happening, and stale narrow-block decisions from before that resize
  // would otherwise stick around.
  const laneRef = useRef<HTMLDivElement | null>(null)
  const [laneWidth, setLaneWidth] = useState(0)

  useEffect(() => {
    const el = laneRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setLaneWidth(el.clientWidth))
    observer.observe(el)
    setLaneWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={
        "flex h-full flex-col overflow-auto rounded-xl border border-gray-200"
      }
    >
      {!showTeamView && !viewAllAllowed && (
        <div className={"border-b border-gray-100 bg-gray-50 px-3 py-2"}>
          <Text type={"secondary"} className={"text-xs"}>
            You&rsquo;re seeing your own schedule. Ask an admin for the calendar
            &ldquo;View all&rdquo; permission to see the whole team&rsquo;s
            availability.
          </Text>
        </div>
      )}

      <Flex
        className={
          "border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-500"
        }
      >
        <div
          className={"sticky left-0 z-10 shrink-0 bg-gray-50 px-3 py-2"}
          style={{ width: NAME_COLUMN_WIDTH }}
        >
          Team member
        </div>
        <Flex className={"flex-1"} ref={laneRef}>
          {hourTicks.map((hour) => (
            <div key={hour} className={"flex-1 px-1 py-2 text-center"}>
              {dayjs().hour(hour).minute(0).format("h A")}
            </div>
          ))}
        </Flex>
      </Flex>

      {loading && (
        <Flex align={"center"} justify={"center"} className={"py-8"}>
          <Spin size={"small"} />
        </Flex>
      )}

      {!loading && error && (
        <Flex align={"center"} justify={"center"} className={"py-8"}>
          <Text type={"danger"} className={"text-sm"}>
            Couldn&rsquo;t load team availability. Try again.
          </Text>
        </Flex>
      )}

      {!loading && !error && !rows.length && (
        <Flex
          vertical
          align={"center"}
          justify={"center"}
          gap={4}
          className={"py-8"}
        >
          <Text type={"secondary"} className={"text-sm"}>
            No active team members available.
          </Text>
          {/* EAD-1293: an invited-but-not-yet-accepted member used to vanish
              with no trace, so an all-pending team read as no team at all. */}
          {pendingCount > 0 && (
            <Text type={"secondary"} className={"text-xs"}>
              {pendingCount} invite{pendingCount === 1 ? "" : "s"} still pending
              — not shown until accepted.
            </Text>
          )}
        </Flex>
      )}

      {!loading && !error && !!rows.length && !totalEvents && (
        <div className={"border-b border-gray-100 px-3 py-2"}>
          <Text type={"secondary"} className={"text-xs"}>
            No scheduled events — everyone is currently free for this day.
          </Text>
        </div>
      )}

      {!loading && !error && !!rows.length && pendingCount > 0 && (
        <div className={"border-b border-gray-100 bg-gray-50 px-3 py-2"}>
          <Text type={"secondary"} className={"text-xs"}>
            {pendingCount} more invite{pendingCount === 1 ? "" : "s"} still
            pending — not shown here until accepted.
          </Text>
        </div>
      )}

      {!loading &&
        !error &&
        rows.map((row) => (
          <AvailabilityRow
            key={row.id}
            name={row.name}
            events={row.events}
            timezone={timezone}
            day={day}
            timeWindow={timeWindow}
            laneWidth={laneWidth}
            onSelectEvent={onSelectEvent}
          />
        ))}
    </div>
  )
}
